import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Download, Share, X, SquarePlus } from "lucide-react";
import {
    canInstall, isIosSafari, isSnoozed, isStandalone, promptInstall,
    snooze, subscribeToInstallState, wasJustInstalled,
} from "./pwaInstall";

// Long enough that it never lands on top of a page still painting itself, and
// short enough that someone doing one quick task still sees it.
const APPEAR_AFTER_MS = 8000;

/**
 * Invites the user to install the app to their device.
 *
 * Two quite different jobs behind one card:
 *  - Chrome/Edge/Android give us a real beforeinstallprompt, so "Install"
 *    opens the browser's own install dialog.
 *  - iOS Safari has no such API. Installing is only possible through
 *    Share ▸ Add to Home Screen, so there the card shows those two steps
 *    rather than a button that could not do anything.
 *
 * Politeness rules (owner decision): never on first paint, never twice at
 * once, and "Not now" means a month of silence rather than a dismissal that
 * is forgotten by the next reload.
 *
 * Rendered into document.body so it escapes the .radix-themes stacking
 * context and cannot be painted over by a Tremor card or the bottom nav.
 */
export default function InstallPrompt() {
    // Re-render whenever the deferred prompt arrives or the app gets installed.
    const installable = useSyncExternalStore(
        subscribeToInstallState,
        canInstall,
        () => false,
    );
    const installed = useSyncExternalStore(
        subscribeToInstallState,
        wasJustInstalled,
        () => false,
    );

    const [waited, setWaited] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [busy, setBusy] = useState(false);

    // iOS state is fixed for the life of the page, so read it once.
    const [ios] = useState(() => isIosSafari() && !isStandalone());

    useEffect(() => {
        if (isStandalone() || isSnoozed()) return; // nothing to ask for
        const t = setTimeout(() => setWaited(true), APPEAR_AFTER_MS);
        return () => clearTimeout(t);
    }, []);

    const show = waited && !dismissed && !installed && (installable || ios);
    if (!show) return null;

    const close = () => {
        snooze();
        setDismissed(true);
    };

    const onInstall = async () => {
        setBusy(true);
        const outcome = await promptInstall();
        setBusy(false);
        // 'accepted' → the appinstalled event hides this anyway. 'dismissed'
        // means they said no in the browser's dialog, which is a clearer no
        // than closing the card, so snooze on both.
        if (outcome !== "unavailable") close();
    };

    return createPortal(
        <div className="install-prompt" role="dialog" aria-labelledby="install-prompt-title">
            <button
                type="button"
                className="install-prompt-close"
                onClick={close}
                aria-label="Not now"
            >
                <X size={16} aria-hidden="true" />
            </button>

            <div className="install-prompt-body">
                <div className="install-prompt-icon" aria-hidden="true">
                    <Download size={20} />
                </div>

                <div className="install-prompt-text">
                    <p className="install-prompt-title" id="install-prompt-title">
                        Install Auto Dealer UG
                    </p>

                    {ios ? (
                        <p className="install-prompt-msg">
                            For a faster, full-screen experience, add it to your Home
                            Screen: tap <Share size={13} className="install-prompt-inline" aria-hidden="true" />
                            {" "}<strong>Share</strong>, then{" "}
                            <SquarePlus size={13} className="install-prompt-inline" aria-hidden="true" />
                            {" "}<strong>Add to Home Screen</strong>.
                        </p>
                    ) : (
                        <p className="install-prompt-msg">
                            Add it to your device for a faster, full-screen experience —
                            it opens straight from your home screen, no browser needed.
                        </p>
                    )}
                </div>
            </div>

            {!ios && (
                <div className="install-prompt-actions">
                    <button type="button" className="install-prompt-later" onClick={close}>
                        Not now
                    </button>
                    <button
                        type="button"
                        className="install-prompt-install"
                        onClick={onInstall}
                        disabled={busy}
                    >
                        {busy ? "Opening…" : "Install"}
                    </button>
                </div>
            )}
        </div>,
        document.body,
    );
}
